import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@tanstack/react-router'

import { useErrands } from '@/api/hooks/useErrands'
import { Button } from '@/components/ui/button'
import { useAppSelector } from '@/redux/hooks'

type Props = {
  onClickSuggestion(suggestion: string): void
  onClickStaticSuggestion(question: string, answer: string): void
}

const routeConfig: Record<string, { suggestionsKey: string; answersKeyPrefix: string }> = {
  '/services': { suggestionsKey: 'aiChat.suggestionsServices', answersKeyPrefix: 'aiChat.answerServices_' },
  '/errands': { suggestionsKey: 'aiChat.suggestionsErrands', answersKeyPrefix: 'aiChat.answerErrands_' },
  '/documents': { suggestionsKey: 'aiChat.suggestionsDocuments', answersKeyPrefix: 'aiChat.answerDocuments_' },
  '/notes': { suggestionsKey: 'aiChat.suggestionsNotes', answersKeyPrefix: 'aiChat.answerNotes_' },
  '/timeline': { suggestionsKey: 'aiChat.suggestionsTimeline', answersKeyPrefix: 'aiChat.answerTimeline_' },
  '/conversations': { suggestionsKey: 'aiChat.suggestionsConversations', answersKeyPrefix: 'aiChat.answerConversations_' },
}

export const PromptSuggestions = ({ onClickSuggestion, onClickStaticSuggestion }: Props) => {
  const errandId = useAppSelector(state => state.settings.selectedErrandId)
  const individualId = useAppSelector(state => state.settings.selectedIndividualId)
  const { data: errands = [] } = useErrands(individualId)
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const errand = useMemo(() => errands.find(e => e.id === errandId), [errands, errandId])

  const { suggestions, answers } = useMemo(() => {
    if (errand) {
      const key = `aiChat.suggestionsErrand_${errand.status}`
      const value = t(key, { returnObjects: true })
      return { suggestions: Array.isArray(value) ? value : [], answers: [] as string[] }
    }

    const matchedRoute = Object.keys(routeConfig).find(route => pathname.startsWith(route))
    if (matchedRoute) {
      const { suggestionsKey, answersKeyPrefix } = routeConfig[matchedRoute]
      const value = t(suggestionsKey, { returnObjects: true })
      const suggestionsList = Array.isArray(value) ? value : []
      const answersList = suggestionsList.map((_, i) => t(`${answersKeyPrefix}${i}`))
      return { suggestions: suggestionsList, answers: answersList }
    }

    const value = t('aiChat.suggestions', { returnObjects: true })
    return { suggestions: Array.isArray(value) ? value : [], answers: [] as string[] }
  }, [errand, pathname, t])

  const handleClick = useCallback(
    (suggestion: string, index: number) => {
      const answer = answers[index]
      if (answer) {
        onClickStaticSuggestion(suggestion, answer)
      } else {
        onClickSuggestion(suggestion)
      }
    },
    [answers, onClickSuggestion, onClickStaticSuggestion],
  )

  return (
    <div className="flex flex-wrap-reverse gap-2">
      {suggestions.map((suggestion, index) => (
        <Button
          key={suggestion}
          onClick={() => handleClick(suggestion, index)}
          size="sm"
          variant="outline"
        >
          {suggestion}
        </Button>
      ))}
    </div>
  )
}
